class OrderCancellationForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.autofillForm();
    this.initScopeToggle();
    this.initFormValidation();
    this.initFormSave();
    this.initDownloadButton();
    this.initCharCounter();
  }
 
  initFormValidation() {
    const form = this.querySelector('form');
    if (!form) return;

    form.noValidate = true;
 
    const watchedInputs = [
      this.querySelector('#cancel-full-name'),
      this.querySelector('#cancel-order-number'),
      this.querySelector('#cancel-email'),
      this.querySelector('#cancel-items'),
    ];
 
    watchedInputs.forEach((input) => {
      if (!input) return;
      input.addEventListener('input', () => this.clearFieldError(input));
    });
 

    form.addEventListener('submit', (event) => {
      const isValid = this.validateForm();
      if (!isValid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.focusFirstError();
      }
    });
  }
 
  validateForm() {
    let isValid = true;
 
    const requiredTextFields = [
      this.querySelector('#cancel-full-name'),
      this.querySelector('#cancel-order-number'),
    ];
 
    requiredTextFields.forEach((input) => {
      if (!input) return;
      if (input.value.trim() === '') {
        this.setFieldError(input, 'required');
        isValid = false;
      } else {
        this.clearFieldError(input);
      }
    });
 
    const emailInput = this.querySelector('#cancel-email');
    if (emailInput) {
      if (emailInput.value.trim() === '') {
        this.setFieldError(emailInput, 'required');
        isValid = false;
      } else if (!this.isValidEmail(emailInput.value)) {
        this.setFieldError(emailInput, 'email');
        isValid = false;
      } else {
        this.clearFieldError(emailInput);
      }
    }
 
    const scopeSelect = this.querySelector('#cancel-scope');
    const itemsInput = this.querySelector('#cancel-items');
    if (scopeSelect && scopeSelect.value === 'partial' && itemsInput) {
      if (itemsInput.value.trim() === '') {
        this.setFieldError(itemsInput, 'required');
        isValid = false;
      } else {
        this.clearFieldError(itemsInput);
      }
    }
 
    return isValid;
  }
 
  setFieldError(input, type) {
    const wrapper = input.closest('.field');
    if (!wrapper) return;
    wrapper.classList.add('field--error');
    // type === 'email' swaps which message span is visible (see CSS).
    wrapper.classList.toggle('field--error-format', type === 'email');
  }
 
  clearFieldError(input) {
    const wrapper = input.closest('.field');
    if (!wrapper) return;
    wrapper.classList.remove('field--error', 'field--error-format');
  }
 
  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }
 
  focusFirstError() {
    const firstError = this.querySelector('.field--error input');
    if (firstError) firstError.focus();
  }
 
  initFormSave() {
    const form = this.querySelector('form');
    if (!form) return;

    form.addEventListener('submit', () => {
      const data = {
        full_name: this.querySelector('#cancel-full-name')?.value || '',
        order_number: this.querySelector('#cancel-order-number')?.value || '',
        email: this.querySelector('#cancel-email')?.value || '',
        cancel_scope: this.querySelector('#cancel-scope')?.value || '',
        items: this.querySelector('#cancel-items')?.value || '',
        submitted_at: new Date().toLocaleString()
      };
      sessionStorage.setItem('cancellation_form_data', JSON.stringify(data));
    });
  }

  initDownloadButton() {
    const downloadBtn = this.querySelector('#download-cancellation-pdf');
    if (!downloadBtn) return;

    downloadBtn.addEventListener('click', () => this.downloadPDF());
  }

  downloadPDF() {
    const raw = sessionStorage.getItem('cancellation_form_data');
    if (!raw) return;

    const data = JSON.parse(raw);
    const wrapper = this.querySelector('.order-cancellation-form-wrapper');
    const labels = {
      name: wrapper?.dataset.name?.toUpperCase() || 'YOUR FULL NAME',
      order: wrapper?.dataset.order?.toUpperCase() || 'ORDER NUMBER',
      email: wrapper?.dataset.email?.toUpperCase() || 'EMAIL ADDRESS',
      items: wrapper?.dataset.items?.toUpperCase() || 'CANCELLED ITEM(S)',
      title: wrapper?.dataset.title || 'Order Cancellation'
    };

    const fullLabel = wrapper?.dataset.full || 'Entire order';
    const partialLabel = wrapper?.dataset.partial || 'Partial order';
    const scopeLabel = data.cancel_scope === 'partial'
      ? `${partialLabel}: ${data.items}`
      : fullLabel;

      const fields = [
        { label: labels.name, value: data.full_name },
        { label: labels.order, value: data.order_number },
        { label: labels.email, value: data.email },
        { label: labels.items, value: scopeLabel }
      ];

    const generatePDF = () => {
      const docDefinition = {
        content: [
          { text: 'MOLTON BROWN', style: 'brand' },
          { text: labels.title, style: 'title' },
          { text: data.submitted_at, style: 'date' },
          {
            canvas: [{
              type: 'line',
              x1: 0, y1: 0,
              x2: 515, y2: 0,
              lineWidth: 0.5,
              lineColor: '#dddddd'
            }],
            margin: [0, 8, 0, 16]
          },
          ...fields.map(({ label, value }) => ([
            { text: label, style: 'fieldLabel' },
            { text: value, style: 'fieldValue' }
          ])).flat()
        ],
        styles: {
          brand:      { fontSize: 10, bold: true, margin: [0, 0, 0, 8] },
          title:      { fontSize: 24, margin: [0, 0, 0, 4] },
          date:       { fontSize: 11, color: '#888888', margin: [0, 0, 0, 8] },
          fieldLabel: { fontSize: 9, color: '#999999', margin: [0, 8, 0, 2] },
          fieldValue: { fontSize: 14, bold: true, margin: [0, 0, 0, 12] }
        },
        defaultStyle: { font: 'Roboto' }
      };

      pdfMake.createPdf(docDefinition).download(`order-cancellation-${data.order_number}-${Date.now()}.pdf`);
    };

    if (window.pdfMake) {
      generatePDF();
      return;
    }

    const pdfMakeScript = document.createElement('script');
    pdfMakeScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/pdfmake.min.js';

    pdfMakeScript.onload = () => {
      const vfsScript = document.createElement('script');
      vfsScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.7/vfs_fonts.js';
      vfsScript.onload = () => generatePDF();
      document.head.appendChild(vfsScript);
    };

    document.head.appendChild(pdfMakeScript);
  }
  initCharCounter() {
  const input = this.querySelector('#cancel-items');
  const counter = this.querySelector('#cancel-items-counter .form-chracter-count');

  if (!input || !counter) return;

  counter.textContent = `${input.value.length}/250`;

  input.addEventListener('input', () => {
    counter.textContent = `${input.value.length}/250`;
  });
}

  autofillForm() {
    const params = new URLSearchParams(window.location.search);
    const orderNumber = params.get('order_id');
    const email = params.get('email');
    const customerName = params.get('customer_name');

    if (orderNumber) {
      const orderInput = this.querySelector('#cancel-order-number');
      if (orderInput) orderInput.value = orderNumber;
    }
    if (email) {
      const emailInput = this.querySelector('#cancel-email');
      if (emailInput) emailInput.value = email;
    }
    if (customerName) {
      const nameInput = this.querySelector('#cancel-full-name');
      if (nameInput) nameInput.value = decodeURIComponent(customerName);
    }
    // Pre-warm captcha
    const firstInput = this.querySelector('#cancel-full-name');
    if (firstInput) {
      firstInput.focus({ preventScroll: true });
      firstInput.blur();
    }
  }

  initScopeToggle() {
    const select = this.querySelector('#cancel-scope');
    if (!select) return;
    select.addEventListener('change', this.togglePartialFieldVisibility.bind(this));
  }

  togglePartialFieldVisibility() {
    const select = this.querySelector('#cancel-scope');
    const itemsWrapper = this.querySelector('#cancel-items-wrapper');
    const itemsInput = this.querySelector('#cancel-items');
    if (!select || !itemsWrapper || !itemsInput) return;

    const isPartial = select.value === 'partial';
    itemsWrapper.classList.toggle('hidden', !isPartial);

    if (!isPartial) {
      itemsInput.value = '';
      this.clearFieldError(itemsInput);
    }
  }
}

customElements.define('order-cancellation-form', OrderCancellationForm);